import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';

/* -------------------
   Global font
   ------------------- */
const GlobalStyles = createGlobalStyle`
  @font-face {
    font-family: 'AnybodyMedium';
    src: url('../assets/fonts/Anybody-Medium.ttf') format('truetype');
    font-weight: 500;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: 'AnybodyBlack';
    src: url('../assets/fonts/Anybody-Black.ttf') format('truetype');
    font-weight: 900;
    font-style: normal;
    font-display: swap;
  }
`;

/* -------------------
   Breakpoints
   ------------------- */
const breakpoints = {
  mobile: '40rem', // 640px
  tablet: '64rem', // 1024px
};

/* -------------------
   Base layout helpers
   ------------------- */
const Page = styled.article`
  max-width: 110rem;
  margin: 0 auto;
  background: #efefef;
  font-family: 'AnybodyMedium', 'Anybody', sans-serif;
  color: #000;
  overflow-x: hidden;
`;

const Hero = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const HeroImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

/* -------------------
   Typography
   ------------------- */
const SubHeading = styled.h2`
  font-family: 'AnybodyBlack', 'Anybody', sans-serif;
  font-weight: 900;
  text-align: center;
  font-size: clamp(1.75rem, 3.5vw, 4rem);
  margin: 2rem auto 1.5rem;
`;

const Para = styled.p`
  font-weight: 400;
  font-size: clamp(1.1rem, 1.6vw, 1.563rem);
  line-height: 1.35;
  max-width: 95ch;
  margin: 1.25rem auto;
  text-align: center;

  &.strong {
    font-weight: 900;
  }
  &.italic {
    font-style: italic;
  }
`;

/* -------------------
   Images & layout
   ------------------- */
const PortraitImg = styled.img`
  width: 100%;
  height: auto;
  max-width: 34rem; /* ≈544px */
  display: block;
  margin: 0 auto;
`;
const PortraitImgOffset = styled(PortraitImg)`
  /* сдвигаем вторую картинку вниз, не увеличивая высоту grid-строки */
  transform: translateY(clamp(3rem, 8vw, 8rem));
`;

const SpotifyFrame = styled.iframe`
  display: block;
  border-radius: 12px;
  width: 100%;
  height: 30rem;
  max-width: 140ch;
  margin: 3rem auto;
`;

const WideImg = styled.img`
  width: 100%;
  height: auto;
  display: block;
  margin: 2rem 0;
`;

const CenteredImg = styled.img`
  width: clamp(15rem, 50vw, 47rem); /* up to ≈751px */
  height: auto;
  display: block;
  margin: 0 auto;
`;

const TwoUp = styled.div`
  display: grid;
  gap: 1rem;
  margin: 0rem auto;
  max-width: 80rem;
  place-items: center;
  grid-template-columns: 1fr;

  @media (min-width: ${breakpoints.mobile}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const BlackBar = styled.div`
  background: #000;
  width: 100%;
  height: clamp(6rem, 10vw, 10.375rem);
  margin-top: 4rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FooterBar = styled.footer`
  background: #000;
  padding: clamp(3rem, 1vh, 3rem) 0;
  text-align: center;

  h1 {
    font-family: 'AnybodyBlack', 'Anybody', sans-serif;
    color: #fff;
    font-size: clamp(2.6rem, 8vw, 8rem);
    font-weight: 900;
    margin: 0;
    line-height: 1;
  }
`;

/* ===============================================================
   Component
   =============================================================== */
export default function Epitaph() {
  return (
    <Page>
      <GlobalStyles />

      {/* Hero Section */}
      <Hero>
        <HeroImg src="/assets/epitaph1.png" alt="Epitaph runway show" />
      </Hero>

      {/* Subtitle */}
      <SubHeading>
        IN CONVERSATION WITH: JEAN LOUIE CASTILLO & KID BRUNSWICK
      </SubHeading>
      <Para className="strong">
        GABRIEL MEALOR‑PRITCHARD CHATS TO JEAN LOUIE CASTILLO ABOUT THE INSPIRATION FOR HIS LATEST FASHION WEEK DEBUT AND WHAT MADE THE DESIGNER PARTNER UNCONDITIONALLY WITH KID BRUNSWICK.
      </Para>
      <Para className="italic">
        PHOTOGRAPHY BY LAUREN CREMER AND RAE TAIT
      </Para>

      {/* Article Body */}
      <Para>
        Designer Jean Louie Castillo presented his debut collection, ‘Epitaph’, at London Fashion Week within the spacious Banking Hall earlier this year. A chillingly haunting experience that unveiled six individual looks resonating with themes of heartbreak and loss as they descended the cascading staircase to the runway, where hundreds looked on in awe.
      </Para>

      <Para>
        Inspired by finding himself undergoing the processes of heartbreak, Epitaph was formed by Castillo’s admiration for cement structures found in Highgate Cemetery in North London, where he frequented “for months,” amidst the breakup period, gathering his thoughts and fathoming the idea for his breakthrough collection. “I loved all of the statues, the angels. I loved the cement, I loved the brutalism, I loved the ivy, and I thought I wanted to make some outfits inspired by this,” the designer told us shortly after the show. “Honestly, I didn’t make any sketches [there], I was making poetry, music, and taking lots of photos, and then the collection just came naturally,” he elaborated, playing with his finger‑length metal rings.
      </Para>

      <Para>
        However, it wasn’t just the clothes that garnered unprecedented appreciation; the music spoke volumes itself. Castillo’s good friend Harry James, who once went by the moniker Kid Brunswick (with his previous persona recently passing on in the artist’s latest track titled ‘DEADFOREVER’), wrote a track exclusively for the show. The artist—who is soon to be reborn—composed a goth‑rock ensemble that complemented the garments to the nth degree. “I’ve admired Jean’s work for a long time — he’s one of those rare creatives whose vision truly moves you,” explains James, on his excitement working with Castillo on this project. “When the opportunity came up, I knew it had to be approached with complete open‑mindedness and a willingness to let go of any conventional structure,” he added.
      </Para>
      {/* Main runway image */}
      <WideImg src="/assets/epitaphmain.png" alt="Epitaph main runway moment" />
      <Para>
        James’ ability to create raw instrumentation and grungy vocals, partnered with Castillo’s keen eye for sinister avant‑garde sculpture, is a match made in heaven. Music can be sound, and fashion can be style, but if they don’t correlate effectively, you’ve missed the mark. The creative arts have a unique ability to transcend boundaries and flow seamlessly between one another. ‘Epitaph’ is the perfect example of just that. “I’m heavily influenced by sound and music in general. I find it vital to incorporate great sound design within my work in some way, and it’s so important to have a soundtrack to my work,” Castillo mentions.
      </Para>

      <Para>
        “We discussed the collection theme and concept. I told him to react to a personal poem I wrote, a by‑product of my painful heartache and design concept. He gave me a week, and the sound was ready,” Jean Louie describes. The track stems from the pain of reality and conveys the intimate exchange between Castillo, his pen, and his paper. James told us, “Jean shared that the show revolved around death, angels, and darkness — three themes that carry so much weight and potential.” Before adding that he wanted to make the music “reflect that gravity while still leaving space for beauty, chaos, and transcendence,” blending his and Castillo’s creative visions into one.
      </Para>



      <Para>
        Complementing one another as they raised the roof sonically and physically. Showing great attention to the reception of his works, James told us that he often imagines “how a listener would react to the music — how it would feel against the skin if it were fabric, or how it would look on a body if it were wearable.” This translation of sound into style, of feeling into form, lies at the heart of this process for James, with each garment correlating to how Castillo notes that “James doesn’t just design for the eye — he designs for the ear, the skin, the pulse.” Taking into consideration the extension of how the music is not only listened to, but embodied through the garments it was composed against.
      </Para>
      <br />
      <br />
      <TwoUp>
        <PortraitImg
          src="/assets/epitaph3.png"
          alt="Epitaph lookbook detail"
        />
        <PortraitImgOffset
          src="/assets/epitaph4.png"
          alt="Epitaph design close-up"
        />
      </TwoUp>
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <br />
      <Para>
        Through his production techniques and process, James proves that music, to him, isn’t all about the sound, more so its reception, how it’s interpreted. “My music has always felt like a living, breathing entity — imperfect, volatile, euphoric. When working with fashion, I think about the visual world the music might exist in: the silhouettes, the textures, the atmosphere. It’s about creating a full sensory experience.” If RIFF is any indication of the creative’s promotion, James isn’t interested in playing by the rules, he’s composing his own. Where he takes us next is anyone’s guess, but one thing’s certain: it’ll be felt, heard, and seen.
      </Para>

      <Para>
        “If someone watched the show on mute, what do you think they’d miss the most about the conversation between the fashion and the music?” we asked the rising designer. “It would not be the same without the music.” Soundscapes played such a vital role within the design process for Jean Louie: “They’d miss the emotions I wanted to capture. They’d miss the emotions Harry delivered through frequency. They’d miss the pain, rage and everything in between the jagged cracks of heartbreak.” Because there is an intricate story to be told, and what better vessel for relaying these sentiments than the use of music, visualised through garment design.
      </Para>

      <Para>
        It is clear to the audience that the garments aren’t just worn, they are performed; they are embodiments of Castillo’s past and present emotions, materialised. Each piece plays its own note, stitched into harmony with the rest, resulting in an ensemble that moves not only with the beat but with intention — the intentions that were written into the soundscape by Brunswick. For him, though his passions lie within film and music, “I haven’t designed garments myself, though I deeply admire fashion as a form of art,” he confirms — the creative demonstrates his understanding that fashion and music are about “identity, emotion, and narrative. Whether it’s a sound or a silhouette, both can communicate something powerful without a single word,” he states, adding, “I see strong parallels” between the two forms of art.
      </Para>

      <Para>
        On the contrary, Jean Louie Castillo has, on occasion, dabbled in the land of songwriting, revealing he has “produced three tracks myself on my phone using BandLab. And I’ve also worked with a producer friend to create a five‑track EP.” But he doesn’t see a definitive correlation between his music creation and his garment construction. “I feel they are separate entities and sonically don’t align, but they both come from the same emotional place,” Castillo explains. Interestingly, Castillo and Brunswick can be found on the same wavelength, playing to their strengths and creating masterpieces that, when partnered, become harmonious as one. But when asked if he ever thinks of his collections as albums, Castillo muses, “I have always pictured my collections as albums, especially now. I find it makes so much sense to compartmentalise them in this way.” They set his works in stone at a certain zeitgeist — something to section, to flick through with ease and put on repeat whenever wanted. “Singers/artists have eras and emotions they choose to channel for each album; this is essentially the same as having a collection. The music blended with the collection is a perfect car crash, and it works,” he goes on to say, consolidating the idea that when he, as a designer, decides to move on or create a different visual aesthetic—as a singer/songwriter would with a change‑up in genre—he is fully able to do so, whilst allowing his past collections to stand in their specific era, untouched by what the future holds. Above all, it showcases the growth of the designer over time — an optical representation of where they have come from and where they are going.
      </Para>

      <br />
      <br />
      <br />
      <CenteredImg
        src="/assets/epitaph5.png"
        alt="Backstage at Epitaph 1"
      />
  <br />
  <br />
  <br />
<Para>
"Half the collection was a response to the music. I wasn’t finished with the collection even after Harry completed the track, and I was drawing and stitching to the track. It was highly emotional for me," Castillo recalls. It is poignant that music holds an incredible weight in the collection. It wouldn't mean the same without it. As an audience member, it was vivid that the clothes and sounds were intertwined, they resonated with emotion, and decorated the room with a personal tale, visualised by the sculptural masterpieces shown that night.
The collection beautifully explored the interdependencies between noise and art. One uplifting the other. The tune shone a light on the collection's history, and in turn, the clothes unveiled layers of narrative that might otherwise have gone unnoticed, as if the music helped the garments to speak and express the emotions each garment possessed. Castillo summarises the experience, reflecting on his successful debut at London Fashion Week. "To watch the looks go down one by one to the sound I made it to is a surreal, euphoric experience that I can’t explain in words." Total elation was reached.
</Para>
<br />
<br />
<br />
<br />
      <Para className="strong">
        JEAN LOUIE CASTILLO AND KID BRUNSWICK SELECT THEIR CURRENT TOP THREE FAVOURITE TRACKS AND GIVE US THE RUNDOWN ON WHY THEY WERE CHOSEN. FIND THE LINKS BELOW:
      </Para>

      <br />
      <br />
      <br />
      <Para className="italic strong">KID BRUNSWICK</Para>

      <Para>
        “Right now, I’m listening to <em>Punish</em> by Ethel Cain — one of the most hauntingly beautiful songs I’ve heard in years. Her work feels honest and fearless.”
        <br />
        <br />
        “Mozart’s <em>Requiem K.626, Rex Tremendae</em> has been a major source of inspiration — there’s a grandeur and depth in that piece that transcends time.”
        <br />
        <br />
        “And <em>Ask Nicely</em> by Ayleen Valentine — a criminally underrated artist whose work I find deeply moving. There’s such a unique tone and vulnerability in her sound.”
      </Para>
      <br />
      <br />
      <br />


      <Para className="italic strong">JEAN LOUIE CASTILLO</Para>
        <br />
        <br />
  
      <Para>
        <em>Bond of Love</em> by Vamp Priest
        <br />
        <em>Rolling Out the Red Carpet</em> by Hail The Sun
        <br />
        <em>Vanish Into You</em> by Lady Gaga
        <br />
        <br />
        <br />
        “Vamp Priest has the ability to add sensuality to macabre lyricism. He captures the sex appeal of gothic and morbid horror fantasies. He knows how to yearn and the power it can hold over someone.”
        <br />
        <br />
        “<em>Rolling Out the Red Carpet</em> is designed like a cinematic and villainous track that also feels deeply nostalgic and painful. It feels like revenge. It feels like helplessness. Then it builds up into explosive rage by the end.”
        <br />
        <br />
        “Lady Gaga’s <em>Vanish into You</em> is reminiscent of 1980s ballads. The love it portrays is one of intense devotion — transcendent and overwhelming. It feels like a complete surrender to love.”
      </Para>
      <SpotifyFrame
        src="https://open.spotify.com/embed/playlist/4Eb3yJ2aevSVya7Hg29Ru6?utm_source=generator&theme=0"
        frameBorder="0"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        allowFullScreen
        loading="lazy"
      />
      {/* Footer */}
   
      <FooterBar>
        <h1>RIFF MAGAZINE</h1>
      </FooterBar>
    </Page>
  );
}
